'use client'

import React, {ReactNode, useContext, useMemo, useRef, useState} from 'react'
import {ProductGridContext} from '@local/stores/contexts'
import {Button, Checkbox, Form, Locale, Pastil, SelectOptionType, Slideout, SlideshowNative, SmallText, Tag} from '@local/ui/components'

import {Accordion as AccordionContainer, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel, AccordionItemState} from '@gnu/react-accessible-accordion'
import {cx} from '@local/utils/cx'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {useDevice, useTranslate} from '@local/hooks'
import {locale} from '@local/stores/dictionaries/route'
import {getStartEndDate} from '@local/utils/date'
import {ProductGridProps} from '@local/ui/blocks/productGrid/ProductGrid'

export interface ProductGridFilterProps {
	items: ProductGridProps['grid']
	regions: SelectOptionType[]
	dates: Array<{startDate: Date, endDate: Date, date:string}>
	productCategories: Array<{label:string; description:string; value: string; count:number, comingSoon:boolean}>
	maxGuests: number
	onSubmit: (items: ProductGridProps['grid']) => void;
	calendarNotice?: string
}

type FilterStateType = {productCategory: string[]; date: string[];region: string[]}

export const ProductGridFilter = ({items, onSubmit, productCategories, dates, regions, calendarNotice}:ProductGridFilterProps) => {
	const translate = useTranslate()
	const {isFilterPanelOpen, setFilterPanel} = useContext(ProductGridContext)
	const onClickClose = () => setFilterPanel(false)

	const device = useDevice()
	const isDesktop = device === 'desktop' ? true : undefined

	const slideshowRef = useRef<HTMLDivElement & {scrollToIndex(index:number)}>(null)

	const filterState = useRef<FilterStateType>({productCategory: [], date: [], region: []})
	const [filterItems, setFilterItems] = useState(items)

	const months = useMemo(() =>
		dates.sort((a, b) => a.endDate - b.endDate).reduce((uniqueMonths: string[], item) => {
			const startMonth = item.startDate.toLocaleDateString(locale.current, {month: 'long', year: 'numeric'}).toLowerCase()
			const endMonth = item.endDate.toLocaleDateString(locale.current, {month: 'long', year: 'numeric'}).toLowerCase()

			if (startMonth === endMonth && !uniqueMonths.includes(startMonth)) {
				uniqueMonths.push(startMonth)
			}

			return uniqueMonths
		}, []),
	[dates])

	const dateOptions = useMemo(() => dates.map(item =>
		({value: `${item.startDate}--${item.endDate}`, label: item.date}),
	), [dates])

	const handleReset = () => {
		filterState.current = {productCategory: [], date: [], region: []}
		setFilterItems(items)
		if (slideshowRef.current) {
			slideshowRef.current.scrollToIndex(0)
		}
	}

	const onChange = () => {
		const	{productCategory, date, region} = filterState.current
		const _filterItems = items.filter(({item}) => {
			// filter by category
			if (productCategory && productCategory.length > 0 && !item.productCategories?.some(category => productCategory.includes(category.value))) {
				return false
			}

			// Filter by date
			if (date && date.length > 0) {
				if (!item?.store?.dates) return false

				const filterDates = Array.isArray(date) ? date : [date]
				const dateFilterPassed = filterDates.some(_date => item?.store?.dates.some(itemDate => {
					const [startDate, endDate] = itemDate.toLowerCase().split(/-| to /)
					const [formattedStartDate, formattedEndDate] = getStartEndDate(startDate, endDate)
					const [calendarStartDate, calendarEndDate] = _date.split('--')
					const [formattedCalendarStartDate, formattedCalendarEndDate] = getStartEndDate(calendarStartDate, calendarEndDate)
					return formattedStartDate.toLocaleDateString(locale.current) === formattedCalendarStartDate.toLocaleDateString(locale.current) &&
                formattedEndDate.toLocaleDateString(locale.current) === formattedCalendarEndDate.toLocaleDateString(locale.current)
				}))

				// If date filter passed, continue to check region
				if (!dateFilterPassed) return false
			}

			// Filter by region
			if (region && region.length > 0 && item.region && !region.includes(item.region)) {
				return false
			}

			return true
		})
		setFilterItems(_filterItems)
	}

	const handleFilterChange = (e) => {
		const {name, value} = e.target
		const _filterState = {...filterState.current}
		const index = _filterState[name].indexOf(value)

		if (index > -1) {
			_filterState[name].splice(index, 1)
		} else if (e.target.checked) {
			_filterState[name].push(value)
		}

		filterState.current = _filterState
		onChange()
	}

	const onSubmitLocal = () => {
		onSubmit(filterItems)
		onClickClose()
	}

	return (
		<Slideout
			isOpen={isFilterPanelOpen}
			variant='right'
			className='bg-grey-4 text-white'
			containerClassName='[&>.slideout-container]:lg:!span-w-6-wide'
			onClickClose={onClickClose}>
			{/* Header */}
			<div className='flex items-center justify-between w-full pt-16 pb-10 lg:py-20'>
				<h3 className='w-fit uppercase'><Locale>Filter</Locale></h3>
				<Button
					small
					variant='icon'
					onClick={onClickClose}
					className='px-16 aspect-square'>
					<CloseSVG />
				</Button>
			</div>

			{/* Body */}
			<Form
				onSubmit={onSubmitLocal}
				autoReset={false}
				additionalReset={handleReset}
				className='flex flex-col overflow-y-auto h-full'>
				<AccordionContainer
					allowZeroExpanded
					allowMultipleExpanded
					className={cx('w-full [&_.accordion__panel-wrapper]:block mb-16')}>

					{/* category */}
					<AccordionFilterItem isExpanded={isDesktop} title={translate('Category')} className='grid grid-cols-2 gutter-gap-1 pb-20 lg:pb-24'>
						{productCategories.map((category, index) => (
							<div className='flex flex-col' key={`category-${index}`}>
								<Checkbox
									name='productCategory'
									id={`filter-category-${index}`}
									value={category.label}
									label={category.value}
									onChange={handleFilterChange}
									disabled={category.comingSoon}
									checkboxClassName='peer-checked:bg-grey-4'
									className='my-10 text-white capitalize'>
									<span className='relative'>{category.label}</span>
									{category.count ? <span>&nbsp;({category.count})</span> : <></>}
									{category.comingSoon && <Pastil variant='grey' className='inline-block ml-10'><Locale>Coming Soon</Locale></Pastil>}
								</Checkbox>
								{category?.description &&	<span className='text-9'>{category.description}</span>}
							</div>

						))}
					</AccordionFilterItem>

					{/* Dates */}
					<AccordionFilterItem
						title={translate('Dates')}
						isExpanded={isDesktop}
						className='pb-20 lg:pb-24'>
						<SlideshowNative
							ref={slideshowRef}
							withPrevNextNavigation
							containerClassName='relative w-full overflow-clip border border-grey-2 rounded-xs'
							className='pt-20 w-full gutter-gap-1'
							prevNextButtonVariant='none'
							prevNextButtonClassName='h-24 w-48 grid place-content-center m-0 bg-grey-4 disbaled:bg-grey-4'
							prevNextNavigationClassName={cx(
								'absolute top-20 left-1 right-1 mt-0 lg:flex',
								'flex flex-row justify-between items-center',
							)}>
							{months.map((month, index) => (
								<div key={`date-${index}`} className='span-w-12 lg:w-full'>
									<p className='w-full flex flex-row justify-center capitalize px-12 text-12'>
										{month}
									</p>
									<ul className={cx(
										'grid p-10 pt-20 gutter-gap-1 lg:gap-10',
										'grid-cols-[repeat(auto-fit,minmax(calc(5*var(--column)+4*var(--gutter)),1fr))]',
										'lg:grid-cols-[repeat(auto-fit,minmax(calc(1*var(--column)+2*var(--gutter)),1fr))]',
									)}>
										{dateOptions
											.filter(item => month.split(' ')[0] === item.label.split(' ')[0])
											.map((item, index, arr) => (
												<Checkbox
													key={`date-${index}`}
													name='date'
													id={item.value}
													value={item.value}
													onChange={handleFilterChange}
													className={cx(
														'cursor-pointer',
														'span-min-w-5 lg:span-min-w-1-wider lg-max:w-full h-112 flex realtive z-1',
														arr.length < 5 ? 'h-112 lg:h-155' : 'h-70 lg:min-h-75 lg:h-full',
														arr.length % 2 !== 0 && index === arr.length - 1 && 'lg-max:col-span-2',
													)}
													checkboxClassName='hidden'
													labelClassName={cx(
														'text-12 capitalize w-full text-white user-select-none',
														'border border-grey-2 rounded-xs',
														'grid place-content-center h-full',
														'peer-focus-visible:border-grey-1 peer-checked:bg-white peer-checked:text-black peer-checked:border-white',
													)}>
													{item.label}
												</Checkbox>
											))}
									</ul>
								</div>
							))}
						</SlideshowNative>
						{calendarNotice && (<SmallText textColor='grey-1' className='mt-12 lg:mt-24'>{calendarNotice}</SmallText>)}
					</AccordionFilterItem>

					{/* Max Guests */}
					{/* <AccordionFilterItem
						title={translate('Guests')}
						isExpanded={isDesktop}
						key={`guests-${isDesktop}`}
						className='lg-max:pb-20 lg:flex lg:items-center lg:h-full'
						headingClassName='lg:py-0 lg:h-full'
						containerClassName='lg:grid lg:grid-cols-2 lg:gutter-gap-1 lg:pt-20 lg:pb-24'>
						<Select
							theme='dark'
							name='guests'
							placeholder={translate('Guests Total')}
							options={[...Array(maxGuests)].map((_, index) => ({
								label: `${index + 1} ${index > 0 ? translate('Guests').toUpperCase() : translate('Guest').toUpperCase()}`,
								value: index + 1,
							}))}
							containerClassName='w-full border-0 p-1'
							className='border'
							keepPrefixIcon
							prefixIcon={<PersonSVG className='w-16 h-16 mr-4 shrink-0' />} />
					</AccordionFilterItem> */}

					{/* Region */}
					<AccordionFilterItem title={translate('Where')} className='pb-20 grid grid-cols-2 gutter-gap-1 lg:pb-24' isExpanded={isDesktop}>
						{regions.map((region, index) => (
							<Checkbox
								key={`region-${index}`}
								name='region'
								id={`filter-region-${index}`}
								value={region.value}
								label={region.label}
								checked={filterState.current.region.includes(region.value)}
								onChange={handleFilterChange}
								checkboxClassName='peer-checked:bg-grey-4'
								className='my-10 text-white capitalize [&>i]:peer-checked:bg-grey-4'>
								{region.label}
							</Checkbox>
						))}
					</AccordionFilterItem>
				</AccordionContainer>

				<div className='grid grid-cols-2 gutter-gap-1 pt-12 pb-20 mt-auto sticky bottom-0 bg-grey-4'>
					<Button type='reset' onClick={handleReset} variant='primary-transparent' small={!isDesktop}>
						<Locale>Clear All</Locale>
					</Button>
					<Button type='submit' variant='secondary' small={!isDesktop} disabled={!filterItems.length}>
						<Locale>Apply</Locale>
						<span className='ml-[1ch]'>({filterItems.length})</span>
					</Button>
				</div>
			</Form>
		</Slideout>
	)
}

interface AccordionFilterItemProps {
	title: string
	className?: string
	headingClassName?: string
	containerClassName?: string
	isExpanded?: boolean
	children: ReactNode
}

const AccordionFilterItem = ({title, children, className, headingClassName, containerClassName, isExpanded = undefined}: AccordionFilterItemProps) => (
	<AccordionItem
		key={`accordion-item-${title}`}
		uuid={`accordion-item-${title}`}
		dangerouslySetExpanded={isExpanded}
		className={cx('lg-max:border-b border-grey-1/30', containerClassName)}>
		<AccordionItemHeading
			aria-level={2}
			id={`accordion-item-${title}-heading`}>
			<AccordionItemButton
				className={cx('flex flex-row items-center justify-between py-20', headingClassName)}>
				<span className='text-11 uppercase tracking-wider'>
					<Locale>{title}</Locale>
				</span>
				<Tag className='bg-transparent underline underline-offset-4 lg:hidden' theme='dark'>
					<AccordionItemState>
						{({expanded}) => expanded
							? <Locale>Close</Locale>
							: <Locale>Select</Locale>}
					</AccordionItemState>
				</Tag>
			</AccordionItemButton>
		</AccordionItemHeading>
		<AccordionItemPanel animated className={className}>
			{children}
		</AccordionItemPanel>
	</AccordionItem>
)
